class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.shopify-section-header');
    this.headerBounds = {};
    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    this.header.classList.add(
      'shopify-section-header-hidden',
      'shopify-section-header-sticky',
    );
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    this.header.classList.add('shopify-section-header-sticky', 'animate-popup');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    this.header.classList.remove(
      'shopify-section-header-hidden',
      'shopify-section-header-sticky',
      'animate-popup',
    );
  }

  closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal =
      this.searchModal || this.header.querySelector('details-modal');
    this.searchModal.close(false);
  }
}

customElements.define('sticky-header', StickyHeader);

class StaticHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.shopify-section-header');
    this.header.classList.add('shopify-section-header--relative');
  }
}

customElements.define('static-header', StaticHeader);

let headerElement = null;
let announcementBarElement = null;
let headerDropdownMenus = null;
let navigationResizeObserver = null;

const HEIGHT_CHANGE_THRESHOLD = 2;

function initializeDOMCache() {
  headerElement = document.querySelector('.header-wrapper');
  // The sticky promo bars sit above the header on every scroll position, so
  // they take priority over the (non-sticky) theme announcement bar.
  announcementBarElement =
    document.querySelector('.dermalux-promo-bars') ||
    document.querySelector('.announcement-bar');
  headerDropdownMenus = document.querySelectorAll('.header__dropdown-menu');
}

function getHeaderMeasurements() {
  const announcementBarHeight = Math.round(
    announcementBarElement?.getBoundingClientRect().height || 0,
  );
  const wrapperHeight = Math.round(
    headerElement?.getBoundingClientRect().height || 0,
  );
  const drawer = headerElement?.querySelector('.header__mega-drawer');
  const drawerHeight = Math.round(drawer?.getBoundingClientRect().height || 0);
  const header = document.querySelector('.header--has-mega-drawer');
  const isMegaOpen =
    document.body.classList.contains('has-header-mega-open') ||
    header?.classList.contains('header--nav-open');
  const headerBarHeight = isMegaOpen
    ? Math.max(wrapperHeight - drawerHeight, 0)
    : wrapperHeight;

  return {
    announcementBarHeight,
    headerBarHeight,
    headerHeight: wrapperHeight,
    navOffsetStart: wrapperHeight + announcementBarHeight,
  };
}

function initializeHeaderPosition() {
  headerDropdownMenus?.forEach((menuList) => {
    setHeaderOverflow(menuList);
  });
}

function setHeaderOverflow(menuList) {
  if (
    !window.navigationSize ||
    typeof window.navigationSize.navOffsetStart === 'undefined'
  ) {
    return;
  }

  const menuHeight = menuList.scrollHeight;
  const headerOffset = window.navigationSize.navOffsetStart;

  if (menuList && window.innerHeight < menuHeight + headerOffset) {
    menuList.style.overflowY = 'auto';
  } else if (menuList) {
    menuList.style.overflowY = 'hidden';
  }
}

function hasMeaningfulSizeChange(next, previous) {
  if (!previous) return true;

  return (
    Math.abs(previous.headerHeight - next.headerHeight) >=
      HEIGHT_CHANGE_THRESHOLD ||
    Math.abs(previous.headerBarHeight - next.headerBarHeight) >=
      HEIGHT_CHANGE_THRESHOLD ||
    Math.abs(previous.announcementBarHeight - next.announcementBarHeight) >=
      HEIGHT_CHANGE_THRESHOLD
  );
}

function updateNavigationSize(options = {}) {
  const { force = false } = options;

  if (window.headerMegaMenuTransitioning && !force) return;
  if (document.body.classList.contains('has-header-mega-open') && !force) {
    return;
  }
  if (updateNavigationSize.isUpdating) return;

  updateNavigationSize.isUpdating = true;

  requestAnimationFrame(() => {
    const next = getHeaderMeasurements();
    const previous = window.navigationSize;

    if (!force && !hasMeaningfulSizeChange(next, previous)) {
      updateNavigationSize.isUpdating = false;
      return;
    }

    window.navigationSize = next;

    const docEl = document.documentElement.style;
    docEl.setProperty('--header-height', `${next.headerHeight}px`);
    docEl.setProperty('--header-bar-height', `${next.headerBarHeight}px`);
    docEl.setProperty(
      '--announcement-bar-height',
      `${next.announcementBarHeight}px`,
    );
    docEl.setProperty('--nav-offset-start', `${next.navOffsetStart}px`);

    document.dispatchEvent(
      new CustomEvent('navigationSizeUpdated', {
        detail: {
          ...next,
          isUpdated: true,
        },
      }),
    );

    updateNavigationSize.isUpdating = false;
  });
}

function observeNavigationElements() {
  const elements = [headerElement, announcementBarElement].filter(Boolean);

  if (elements.length === 0) return;

  const debouncedUpdateNavigationSize = window.debounce(
    () => updateNavigationSize(),
    250,
  );

  navigationResizeObserver = new ResizeObserver(() => {
    debouncedUpdateNavigationSize();
  });

  elements.forEach((element) => navigationResizeObserver.observe(element));

  updateNavigationSize({ force: true });
}

document.addEventListener('navigationSizeUpdated', initializeHeaderPosition);

document.addEventListener('headerMegaMenuMeasure', () => {
  updateNavigationSize({ force: true });
});

function initializeUpdateNavigationSize() {
  if ('ResizeObserver' in window) {
    observeNavigationElements();
    window.addEventListener(
      'resize',
      window.debounce(() => updateNavigationSize(), 250),
    );
  } else {
    updateNavigationSize({ force: true });
    window.addEventListener(
      'resize',
      window.debounce(() => updateNavigationSize(), 250),
    );
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initializeDOMCache();
  initializeUpdateNavigationSize();
});

window.addEventListener('load', () => updateNavigationSize({ force: true }));
